import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Crown, Zap, Calendar, Rocket, Users, Gift, Flag, Check, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BENEFIT_ICONS = { 0: Crown, 1: Rocket, 2: Sparkles, 3: Zap, 4: Flag, 5: Gift, 6: Calendar, 7: Users };

/**
 * Vendor-only Founding Vendor CTA + purchase flow.
 * Shown to EVERY vendor (both founding and non-founding) inside the dashboard
 * as a dedicated gold tab. If already a Founding Vendor, shows a thank-you
 * panel with their number and story editor. Otherwise shows urgency meter +
 * Stripe checkout button.
 */
export default function FoundingVendorTab() {
  const { getAuthHeaders } = useAuth();
  const [status, setStatus] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);
  const [story, setStory] = useState('');
  const [savingStory, setSavingStory] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [statusRes, meRes] = await Promise.all([
        fetch(`${API}/founding-vendors/status`),
        fetch(`${API}/vendors/me`, { headers: getAuthHeaders() }),
      ]);
      if (statusRes.ok) setStatus(await statusRes.json());
      if (meRes.ok) {
        const v = await meRes.json();
        setVendor(v);
        setStory(v?.founding_vendor_story || '');
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Handle Stripe return
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const founding = params.get('founding');
    if (sessionId && founding === 'success' && vendor) {
      (async () => {
        const res = await fetch(`${API}/founding-vendors/confirm/${sessionId}`, {
          method: 'POST',
          headers: getAuthHeaders(),
        });
        if (res.ok) {
          const d = await res.json();
          if (d.status === 'paid') {
            toast.success(`🥇 Welcome, Founding Vendor #${d.founding_number}!`);
            // Clean URL
            window.history.replaceState(null, '', window.location.pathname);
            load();
          }
        }
      })();
    }
  }, [vendor]);

  const startCheckout = async () => {
    setCheckingOut(true);
    try {
      const res = await fetch(`${API}/founding-vendors/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          success_url: `${window.location.origin}/dashboard/vendor/founding?founding=success`,
          cancel_url: `${window.location.origin}/dashboard/vendor/founding?founding=cancelled`,
        }),
      });
      const data = await res.json();
      if (res.ok && data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error(data?.detail || 'Could not start checkout');
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setCheckingOut(false);
    }
  };

  const saveStory = async () => {
    setSavingStory(true);
    try {
      const res = await fetch(`${API}/founding-vendors/me/story`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ story }),
      });
      if (res.ok) toast.success('Story saved');
      else toast.error('Failed to save');
    } finally {
      setSavingStory(false);
    }
  };

  if (loading) return <div className="text-[#9CA3AF] p-12 text-center">Loading…</div>;
  if (!status) return null;

  const isFounder = vendor?.is_founding_vendor;
  const pct = Math.min(100, (status.slots_taken / status.slots_total) * 100);

  return (
    <div className="space-y-6 max-w-4xl" data-testid="founding-vendor-tab">
      {/* Hero */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-3xl border border-[#F59E0B]/40 p-8 sm:p-12"
        style={{
          background: 'linear-gradient(135deg, rgba(245,158,11,0.15) 0%, rgba(202,138,4,0.10) 50%, rgba(12,10,9,1) 100%)',
          boxShadow: '0 30px 80px -20px rgba(245,158,11,0.35)',
        }}
      >
        {/* background glint */}
        <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-[#F59E0B]/20 blur-[120px]" />

        <div className="relative">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F59E0B]/20 border border-[#F59E0B]/40 text-[#FCD34D] text-xs font-bold mb-5">
            <Crown size={12} /> LIMITED TO 50 VENDORS · CROWDFUNDING TO GO GLOBAL
          </div>

          <h1 className="font-syne text-3xl sm:text-5xl font-bold text-white mb-3">
            Become a <span className="text-[#FCD34D]">Founding Vendor</span>
          </h1>
          <p className="text-[#C4B5FD] text-base sm:text-lg mb-6 max-w-2xl">
            Help us take EntrepreNatives global. For a one-time <strong className="text-[#FCD34D]">${status.price_usd}</strong>, you become one of only <strong className="text-[#FCD34D]">50</strong> early vendors who will forever be celebrated as co-builders of this platform.
          </p>

          {/* Slot meter */}
          <div className="mb-6 max-w-md">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-[#9CA3AF] font-semibold uppercase tracking-wide">
                Spots remaining: <span className="text-[#FCD34D] font-bold">{status.slots_remaining}</span> / {status.slots_total}
              </span>
              <span className="text-xs text-[#9CA3AF] font-mono" data-testid="founding-slot-count">
                {status.slots_taken}/{status.slots_total}
              </span>
            </div>
            <div className="h-3 rounded-full bg-white/5 border border-white/10 overflow-hidden">
              <motion.div
                className="h-full"
                style={{ background: 'linear-gradient(90deg, #F59E0B, #FCD34D)' }}
                initial={{ width: 0 }}
                animate={{ width: `${pct}%` }}
                transition={{ duration: 0.8 }}
              />
            </div>
          </div>

          {isFounder ? (
            <div className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#10B981]/15 border border-[#10B981]/40 text-[#86EFAC] font-semibold" data-testid="founding-active">
              <Check size={16} /> You are Founding Vendor #{vendor.founding_vendor_number} — thank you.
            </div>
          ) : status.is_sold_out ? (
            <div className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-white/5 border border-white/10 text-[#9CA3AF] font-semibold">
              <X size={16} /> All 50 slots filled. Thank you!
            </div>
          ) : (
            <button
              onClick={startCheckout}
              disabled={checkingOut}
              className="inline-flex items-center gap-3 px-8 py-4 rounded-xl text-white text-base font-bold shadow-[0_10px_40px_-10px_rgba(245,158,11,0.6)] disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)' }}
              data-testid="founding-checkout-btn"
            >
              {checkingOut ? <><Loader2 className="animate-spin" size={18} /> Redirecting…</> : <><Crown size={18} /> Claim Founding Vendor Spot · ${status.price_usd}</>}
            </button>
          )}
        </div>
      </motion.div>

      {/* Benefits grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="founding-benefits">
        {status.benefits.map((b, i) => {
          const Icon = BENEFIT_ICONS[i] || Sparkles;
          return (
            <div key={i} className="p-4 rounded-2xl bg-white/5 border border-white/10 flex items-start gap-3">
              <div className="w-9 h-9 shrink-0 rounded-xl bg-[#F59E0B]/15 border border-[#F59E0B]/30 flex items-center justify-center">
                <Icon size={16} className="text-[#FCD34D]" />
              </div>
              <div>
                <div className="text-white font-semibold text-sm mb-1">{b.title}</div>
                <div className="text-[#9CA3AF] text-xs">{b.desc}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Value stack */}
      <div className="p-5 rounded-2xl bg-[#8B5CF6]/5 border border-[#8B5CF6]/20">
        <p className="text-[#C4B5FD] text-sm">
          <strong className="text-white">Est. value: $900+</strong> · Price: <strong className="text-[#FCD34D]">${status.price_usd}</strong> — one-time, never a renewal.
        </p>
      </div>

      {/* Story editor for founders */}
      {isFounder && (
        <div className="p-5 rounded-2xl bg-white/5 border border-white/10" data-testid="founding-story-editor">
          <h3 className="font-syne text-lg font-bold text-white mb-2">Your Founding Vendor Story</h3>
          <p className="text-[#9CA3AF] text-xs mb-3">
            This is what shows up on our <a className="text-[#FCD34D] underline" href="/founding-vendors" target="_blank" rel="noreferrer">appreciation page</a> — your origin, your mission, why you joined us early. 2 000 characters max.
          </p>
          <textarea
            value={story}
            onChange={(e) => setStory(e.target.value.slice(0, 2000))}
            className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-white text-sm min-h-[140px]"
            placeholder="Tell the story of your business, why you chose to be one of the first, and what Indigenous entrepreneurship means to you…"
            data-testid="founding-story-textarea"
          />
          <div className="flex items-center justify-between mt-2">
            <span className="text-[10px] text-[#64748B]">{story.length}/2000</span>
            <button
              onClick={saveStory}
              disabled={savingStory}
              className="px-4 py-2 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] disabled:opacity-50"
              data-testid="save-founding-story"
            >
              {savingStory ? 'Saving…' : 'Save Story'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
